/**
 * UserDetailsDrawer Component
 * Slide-out panel with full user information
 */

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Switch,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  CalendarToday as DateIcon,
  VpnKey as KeyIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  LinkOff as DisconnectIcon,
} from '@mui/icons-material';
import UserStatusBadge from './UserStatusBadge';
import { format } from 'date-fns';
import { useApi } from '../../hooks/useApi';

interface UserDetails {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  login_count: number;
  connections: Array<{
    type: string;
    created_at: string;
  }>;
  llmActivity: {
    totalRequests: number;
    totalCost: number;
    lastRequest: string | null;
  };
}

interface UserDetailsDrawerProps {
  open: boolean;
  userId: string | null;
  onClose: () => void;
  onUserUpdated: () => void;
}

const UserDetailsDrawer: React.FC<UserDetailsDrawerProps> = ({
  open,
  userId,
  onClose,
  onUserUpdated,
}) => {
  const api = useApi();
  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetMcpDialogOpen, setResetMcpDialogOpen] = useState(false);
  const [mcpUrl, setMcpUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open && userId) {
      fetchUserDetails();
    }
  }, [open, userId]);

  const fetchUserDetails = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!user) return;
    
    setUpdating(true);
    try {
      await api.put(`/admin/users/${user.id}`, {
        is_active: !user.is_active,
      });
      setUser({ ...user, is_active: !user.is_active });
      onUserUpdated();
    } catch (error) {
      console.error('Failed to update user:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleAdmin = async () => {
    if (!user) return;
    
    setUpdating(true);
    try {
      await api.put(`/admin/users/${user.id}`, {
        is_admin: !user.is_admin,
      });
      setUser({ ...user, is_admin: !user.is_admin });
      onUserUpdated();
    } catch (error) {
      console.error('Failed to update user:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!user) return;
    
    setUpdating(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      setDeleteDialogOpen(false);
      onClose();
      onUserUpdated();
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleResetMcp = async () => {
    if (!user) return;
    
    setUpdating(true);
    try {
      const response = await api.post(`/admin/users/${user.id}/reset-mcp`);
      setMcpUrl(response.data.mcpUrl);
      setResetMcpDialogOpen(false);
    } catch (error) {
      console.error('Failed to reset MCP token:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDisconnectService = async (serviceType: string) => {
    if (!user) return;
    
    setUpdating(true);
    try {
      await api.delete(`/admin/users/${user.id}/connections/${serviceType}`);
      setUser({
        ...user,
        connections: user.connections.filter(c => c.type !== serviceType),
      });
      onUserUpdated();
    } catch (error) {
      console.error('Failed to disconnect service:', error);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return format(new Date(date), 'MMM d, yyyy HH:mm');
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400, md: 500 } },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">User Details</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          ) : user ? (
            <>
              {mcpUrl && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    New MCP URL: {mcpUrl}
                  </Typography>
                </Alert>
              )}

              <Stack spacing={3}>
                {/* Basic Info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Account Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText 
                        primary="Email"
                        secondary={user.email}
                      />
                    </ListItem>
                    <ListItem>
                      <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText 
                        primary="Full Name"
                        secondary={user.full_name || 'Not provided'}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Divider />

                {/* Status */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Status & Permissions
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Account Active" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={user.is_active}
                          onChange={handleToggleActive}
                          disabled={updating}
                          color="success"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Admin Privileges" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={user.is_admin}
                          onChange={handleToggleAdmin}
                          disabled={updating}
                          color="primary"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Email Verified" />
                      <ListItemSecondaryAction>
                        <UserStatusBadge type="verified" value={user.email_verified} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </Box>

                <Divider />

                {/* Dates */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Activity
                  </Typography>
                  <List dense>
                    <ListItem>
                      <DateIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText 
                        primary="Registered"
                        secondary={formatDate(user.created_at)}
                      />
                    </ListItem>
                    <ListItem>
                      <DateIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText 
                        primary="Last Login"
                        secondary={`${formatDate(user.last_login_at)} (${user.login_count} total)`}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Divider />

                {/* Connected Services */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Connected Services ({user.connections.length})
                  </Typography>
                  {user.connections.length > 0 ? (
                    <List>
                      {user.connections.map((connection) => (
                        <ListItem key={connection.type}>
                          <LinkIcon sx={{ mr: 2, color: 'text.secondary' }} />
                          <ListItemText 
                            primary={connection.type}
                            secondary={`Connected ${formatDate(connection.created_at)}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton 
                              edge="end" 
                              onClick={() => handleDisconnectService(connection.type)}
                              disabled={updating}
                              color="error"
                              size="small"
                            >
                              <DisconnectIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      No services connected
                    </Typography>
                  )}
                </Box>

                <Divider />

                {/* LLM Usage */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    LLM Usage
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Total Requests"
                        secondary={user.llmActivity.totalRequests}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Total Cost"
                        secondary={`$${user.llmActivity.totalCost.toFixed(4)}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Last Request"
                        secondary={formatDate(user.llmActivity.lastRequest)}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Divider />

                {/* Actions */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Actions
                  </Typography>
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<KeyIcon />}
                      onClick={() => setResetMcpDialogOpen(true)}
                      disabled={updating}
                    >
                      Reset MCP Token
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={updating}
                    >
                      Delete User
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </>
          ) : (
            <Typography>User not found</Typography>
          )}
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {user?.email}? This action cannot be undone.
            All user data including connections and history will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset MCP Confirmation Dialog */}
      <Dialog open={resetMcpDialogOpen} onClose={() => setResetMcpDialogOpen(false)}>
        <DialogTitle>Reset MCP Token</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset the MCP token for {user?.email}? 
            They will need to update their Claude Web configuration with the new URL.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetMcpDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetMcp} color="warning" variant="contained">
            Reset Token
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDetailsDrawer;